/**
 * @file Toaster.tsx
 * @brief Renders all queued toasts from `useToast()`. Mount once near
 *        the app root.
 *
 * @author DES
 * @date 26-05-2026
 */

import { Check, Info, TriangleAlert, X } from "lucide-react";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "./Toast";
import { useToast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";

function ToastIcon({
  variant,
}: {
  variant: "default" | "success" | "warning" | "destructive" | null | undefined;
}) {
  const wrap = "flex h-8 w-8 shrink-0 items-center justify-center rounded-full";
  const icon = "h-4 w-4";
  switch (variant) {
    case "success":
      return (
        <span className={cn(wrap, "bg-success/15 text-success")} aria-hidden>
          <Check className={icon} strokeWidth={2.5} />
        </span>
      );
    case "warning":
      return (
        <span className={cn(wrap, "bg-warning/15 text-warning")} aria-hidden>
          <TriangleAlert className={icon} strokeWidth={2.25} />
        </span>
      );
    case "destructive":
      return (
        <span
          className={cn(wrap, "bg-destructive/15 text-destructive")}
          aria-hidden
        >
          <X className={icon} strokeWidth={2.5} />
        </span>
      );
    default:
      return (
        <span
          className={cn(wrap, "bg-muted text-muted-foreground")}
          aria-hidden
        >
          <Info className={icon} strokeWidth={2.25} />
        </span>
      );
  }
}

export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider>
      {toasts.map(({ id, title, description, action, variant, ...props }) => (
        <Toast key={id} variant={variant} {...props}>
          <ToastIcon variant={variant} />
          <div className="grid min-w-0 flex-1 gap-0.5 pt-0.5">
            {title ? <ToastTitle>{title}</ToastTitle> : null}
            {description ? (
              <ToastDescription>{description}</ToastDescription>
            ) : null}
          </div>
          {action}
          <ToastClose />
        </Toast>
      ))}
      <ToastViewport />
    </ToastProvider>
  );
}
